import { Download, RefreshCw } from "lucide-react";
import { EmptyState } from "@/components/brand/empty-state";
import { ErrorState } from "@/components/brand/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { MonthReportPublic } from "@/lib/api";
import { COLUMNS, csvDataUrl, monthLabel, useComputeMonth, useMonthReports } from "../hooks";

/**
 * Every client's figures for one closed month: the numbers a monthly client
 * report is built from, computed by the platform and kept for good. The
 * job works them out on the 3rd; the button does it now.
 */
export function MonthReportTable({ month }: { month: string }) {
  const reports = useMonthReports(month);
  const compute = useComputeMonth(month);
  const label = monthLabel(month);

  if (reports.isPending) return <Skeleton className="h-64 rounded-lg" />;
  if (reports.isError) return <ErrorState onRetry={() => void reports.refetch()} />;

  const rows = reports.data.reports;
  const actions = (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="outline"
        disabled={compute.isPending}
        onClick={() => compute.mutate({ query: { month } })}
      >
        <RefreshCw /> {rows.length ? "Work the figures out again" : "Work the figures out"}
      </Button>
      {rows.length ? (
        <Button asChild>
          <a href={csvDataUrl(month, rows)} download={`groundwork-${month.slice(0, 7)}.csv`}>
            <Download /> Download CSV
          </a>
        </Button>
      ) : null}
    </div>
  );

  if (rows.length === 0) {
    return (
      <EmptyState
        title={`No figures for ${label} yet`}
        description="The figures are worked out on the 3rd of the following month. Work them out now to see them today."
        action={actions}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          {rows.length} {rows.length === 1 ? "client" : "clients"} for {label}.
          {reports.data.missing.length
            ? ` Not yet worked out: ${reports.data.missing.join(", ")}.`
            : ""}
        </p>
        {actions}
      </div>
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <Table aria-label={`Monthly figures for ${label}`}>
          <TableHeader>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableHead
                  key={c.key}
                  className={c.key === "orgName" ? "" : "whitespace-nowrap text-right"}
                >
                  {c.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.orgId}>
                {COLUMNS.map((c) => (
                  <TableCell
                    key={c.key}
                    className={
                      c.key === "orgName"
                        ? "whitespace-nowrap font-medium text-foreground"
                        : "text-right tabular-nums"
                    }
                  >
                    <Cell value={r[c.key]} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Cell({ value }: { value: MonthReportPublic[keyof MonthReportPublic] }) {
  if (value === null || value === undefined)
    return <span className="text-muted-foreground">-</span>;
  if (typeof value === "boolean") return <>{value ? "Yes" : "No"}</>;
  if (typeof value === "number") return <>{value.toLocaleString()}</>;
  return <>{String(value)}</>;
}
